import { type ReactNode, useId } from 'react';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';

import { cardSx, chipSx, wf } from '../shell/tokens';

const intakeJourneyStages = ['agreements', 'intake', 'verification', 'payers', 'import'] as const;
export type IntakeJourneyStage = (typeof intakeJourneyStages)[number];

const labels: Record<IntakeJourneyStage, string> = {
  agreements: 'Agreements & setup',
  intake: 'Intake form',
  verification: 'JotPsych verification',
  payers: 'Payer connections',
  import: 'Import patients',
};

/** Stages that keep their existing feature mounted when journey chrome is off. */
const liveStages: readonly IntakeJourneyStage[] = ['intake', 'payers'];

export interface IntakeJourneyStageState {
  statusLabel: string;
  /** One-line hint under the status, e.g. what the step covers. */
  caption?: string;
  /** Show the step as done. Only the owner of the stage's completion signal may set this. */
  completed?: boolean;
  lockedReason?: string;
}

/** Presentation only. The caller supplies authoritative status and navigation decisions. */
export interface IntakeJourneyShellProps {
  /** Remove journey chrome without remounting existing intake during a gate fallback. */
  showJourneyChrome?: boolean;
  organizationName: string;
  selectedStage: IntakeJourneyStage;
  onNavigate: (stage: IntakeJourneyStage) => void;
  stages: Record<IntakeJourneyStage, IntakeJourneyStageState>;
  content: Record<IntakeJourneyStage, ReactNode>;
  toolbar?: ReactNode;
  kickoff?: ReactNode;
}

function StepMarker({
  index,
  completed,
  locked,
  selected,
}: {
  index: number;
  completed: boolean;
  locked: boolean;
  selected: boolean;
}) {
  const tone = completed
    ? { bgcolor: wf.green, color: wf.surface, borderColor: wf.green }
    : selected
      ? { bgcolor: wf.blue, color: wf.surface, borderColor: wf.blue }
      : locked
        ? { bgcolor: wf.surfaceAlt, color: wf.faint, borderColor: wf.line }
        : { bgcolor: wf.surface, color: wf.muted, borderColor: wf.line };
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        border: '1px solid',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontSize: 13,
        fontWeight: 700,
        ...tone,
      }}
    >
      {completed ? (
        <CheckOutlinedIcon sx={{ fontSize: 16 }} />
      ) : locked ? (
        <LockOutlinedIcon sx={{ fontSize: 14 }} />
      ) : (
        index + 1
      )}
    </Box>
  );
}

/** Keep supplied content mounted across stage changes; the owner retains scope/save guards. */
export function IntakeJourneyShell({
  showJourneyChrome = true,
  organizationName,
  selectedStage,
  onNavigate,
  stages,
  content,
  toolbar,
  kickoff,
}: IntakeJourneyShellProps) {
  const id = useId();
  return (
    <Stack
      spacing={showJourneyChrome ? 3 : 0}
      sx={
        showJourneyChrome
          ? { maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 4 }, color: wf.ink }
          : { display: 'contents' }
      }
    >
      {showJourneyChrome && (
        <Stack spacing={1}>
          <Typography variant="overline" color="text.secondary">
            {organizationName}
          </Typography>
          <Typography variant="h4" component="h1">
            Get billing-ready
          </Typography>
          <Typography color="text.secondary">
            Five steps, then your first claim. Patient import can run alongside the others.
          </Typography>
          {toolbar}
        </Stack>
      )}
      {showJourneyChrome && (
        <Box
          component="nav"
          aria-label="Practice setup stages"
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(5, minmax(0, 1fr))',
            },
            gap: { xs: 1, md: 0 },
            borderBottom: { md: `1px solid ${wf.line}` },
          }}
        >
          {intakeJourneyStages.map((stage, index) => {
            const selected = stage === selectedStage;
            const { statusLabel, caption, completed = false, lockedReason } = stages[stage];
            const locked = Boolean(lockedReason);
            const chipTone = completed ? 'ok' : locked ? 'mut' : selected ? 'info' : 'mut';
            return (
              <ButtonBase
                key={stage}
                id={`${id}-${stage}-button`}
                aria-current={selected ? 'step' : undefined}
                aria-controls={`${id}-${stage}-panel`}
                aria-describedby={`${id}-${stage}-status`}
                aria-disabled={locked}
                onClick={() => {
                  if (!locked) onNavigate(stage);
                }}
                sx={{
                  ...cardSx,
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  textAlign: 'left',
                  gap: 1,
                  border: { xs: `1px solid ${selected ? wf.blue : wf.line}`, md: 0 },
                  borderRadius: { xs: '8px', md: 0 },
                  borderBottom: { md: `3px solid ${selected ? wf.blue : 'transparent'}` },
                  bgcolor: { xs: selected ? wf.blueSoft : wf.surface, md: 'transparent' },
                  color: locked ? wf.muted : wf.ink,
                  '&.Mui-focusVisible': { outline: `3px solid ${wf.blue}`, outlineOffset: 2 },
                }}
              >
                <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', minWidth: 0 }}>
                  <StepMarker
                    index={index}
                    completed={completed}
                    locked={locked}
                    selected={selected}
                  />
                  <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>
                    {labels[stage]}
                  </Typography>
                </Stack>
                <Box sx={{ pl: '38px' }}>
                  <Box component="span" sx={chipSx(chipTone)}>
                    {locked ? 'Locked' : statusLabel}
                  </Box>
                  <Typography
                    id={`${id}-${stage}-status`}
                    variant="caption"
                    component="p"
                    sx={{ color: wf.muted, mt: 0.5, lineHeight: 1.4 }}
                  >
                    {lockedReason ?? caption ?? statusLabel}
                  </Typography>
                </Box>
              </ButtonBase>
            );
          })}
        </Box>
      )}
      {intakeJourneyStages.map(stage => {
        const live = liveStages.includes(stage);
        return (
          <Box
            key={stage}
            component="section"
            id={`${id}-${stage}-panel`}
            aria-labelledby={showJourneyChrome ? `${id}-${stage}-button` : undefined}
            hidden={showJourneyChrome ? stage !== selectedStage : !live}
            sx={{
              minWidth: 0,
              ...(!showJourneyChrome && live ? { display: 'contents' } : {}),
            }}
          >
            {!showJourneyChrome ? (
              live ? (
                content[stage]
              ) : null
            ) : stages[stage].lockedReason ? (
              <Box sx={cardSx}>
                <Typography>{stages[stage].lockedReason}</Typography>
              </Box>
            ) : (
              content[stage]
            )}
          </Box>
        );
      })}
      {showJourneyChrome && kickoff}
    </Stack>
  );
}
